import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

interface RFP {
  id: string;
  title: string;
  project_type: string;
  description: string;
  location: string;
  estimated_volume: number;
  timeline: string;
}

interface SubmitProposalProps {
  isOpen: boolean;
  onClose: () => void;
  rfp: RFP;
  onSuccess: () => void;
}

export const SubmitProposal: React.FC<SubmitProposalProps> = ({ isOpen, onClose, rfp, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    details: '',
    timeline: '',
    budget: '',
    documents: [] as string[]
  });
  const { user } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const { data: proposalInsert, error } = await supabase
        .from('proposals')
        .insert({
          rfp_id: rfp.id,
          developer_id: user.id,
          details: formData.details,
          timeline: formData.timeline,
          budget: formData.budget ? Number(formData.budget) : null,
          documents: formData.documents,
          status: 'pending'
        })
        .select('id')
        .single();

      if (error) throw error;

      toast({
        title: "Proposal Submitted Successfully",
        description: "Your proposal has been sent to the organization for review.",
      });

      // Reset form
      setFormData({
        details: '',
        timeline: '',
        budget: '',
        documents: []
      });

      // Notify RFP owner (entity) of new proposal
      await supabase.rpc('notify_entity_on_proposal', {
        rfp_id_input: rfp.id,
        title_input: 'New Proposal Submitted',
        message_input: 'A project developer has submitted a proposal to your RFP.'
      });

      onSuccess();
    } catch (error: any) {
      console.error('Error submitting proposal:', error);
      toast({
        title: "Error",
        description: "Failed to submit proposal. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Submit Proposal</DialogTitle>
          <DialogDescription>
            Submit your proposal for: {rfp.title}
          </DialogDescription>
        </DialogHeader>

        <div className="mb-4 p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">RFP Details</h4>
          <div className="text-sm space-y-1">
            <div><strong>Project Type:</strong> {rfp.project_type}</div>
            <div><strong>Location:</strong> {rfp.location}</div>
            <div><strong>Estimated Volume:</strong> {rfp.estimated_volume?.toLocaleString()} credits</div>
            <div><strong>Timeline:</strong> {rfp.timeline}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="details">Proposal Details *</Label>
            <Textarea
              id="details"
              value={formData.details}
              onChange={(e) => handleInputChange('details', e.target.value)}
              placeholder="Describe your approach, methodology, team expertise, and how you plan to deliver this project..."
              rows={6}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="timeline">Estimated Timeline</Label>
              <Input
                id="timeline"
                value={formData.timeline}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                placeholder="e.g., 8-12 months"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="budget">Budget (USD)</Label>
              <Input
                id="budget"
                type="number"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                placeholder="e.g., 150000"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Supporting Documents</Label>
            <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
              <p className="text-sm text-muted-foreground">
                Document upload functionality coming soon...
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                For now, mention relevant documents in your proposal details
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Proposal'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};